'use client'
import { Input } from '@repo/ui/components'
import type { OrderBoxAmountInputProps } from './order-box.types'
import {
  formatNumberWithCommasAndDecimals,
  handleNumericInput,
  handlePasteInput,
  removeCommasFromNumber,
} from '@repo/tools'
import { useRef, useState, useEffect, useLayoutEffect } from 'react'
import { cn } from '@repo/ui/lib'

export const OrderBoxAmountInput = ({
  name,
  value,
  onChange,
  max,
  assetLabel,
  onAmountChange,
  targetScale = 1,
  tab,
  placeholder,
  noFocusStyle = false,
  disabled = false,
  scale = 1,
}: OrderBoxAmountInputProps) => {
  const timeout = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isInternalChangeRef = useRef(false)
  const pendingCursorPosRef = useRef<number | null>(null)

  const maxDecimalPlaces = targetScale > 1 ? Math.log10(targetScale) : 0

  // Local state for formatted value
  const [displayValue, setDisplayValue] = useState(() => {
    if (value === undefined || value === 0 || value === null) {
      return ''
    }
    return formatNumberWithCommasAndDecimals(value, maxDecimalPlaces)
  })

  // Update display value when value prop changes externally
  useEffect(() => {
    if (!isInternalChangeRef.current) {
      if (value === undefined || value === 0 || value === null) {
        setDisplayValue('')
      } else {
        setDisplayValue(
          formatNumberWithCommasAndDecimals(value, String(scale).length),
        )
      }
    }
    isInternalChangeRef.current = false
  }, [value, scale])

  // Restore cursor position after React updates the DOM
  useLayoutEffect(() => {
    if (pendingCursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        pendingCursorPosRef.current,
        pendingCursorPosRef.current,
      )
      pendingCursorPosRef.current = null
    }
  })

  return (
    <div className={disabled ? 'opacity-50' : ''}>
      <Input
        ref={inputRef}
        name={name}
        min={0}
        max={max}
        startLabel={assetLabel}
        placeholder={placeholder}
        inputSize='md'
        disabled={disabled}
        onKeyDown={(e) => {
          handleNumericInput(e, 20, /[0-9۰-۹٠-٩.,]/)
        }}
        onPaste={(e) => {
          handlePasteInput(e, 20, /[0-9۰-۹٠-٩.,]/)
        }}
        itemType='text'
        value={displayValue}
        onChange={(e) => {
          isInternalChangeRef.current = true
          const target = e.target as HTMLInputElement
          const inputValue = target.value

          // Check if decimal places exceed allowed limit based on targetScale
          const decimalPart = inputValue.split('.')[1]
          if (decimalPart && decimalPart.length > maxDecimalPlaces) {
            // Don't update if decimal places exceed limit
            return
          }

          if (inputValue.length > 1 && inputValue.endsWith('.')) {
            setDisplayValue(inputValue.toString())
            return
          }

          let cleanValue = removeCommasFromNumber(
            inputValue,
          ) as unknown as number

          // Apply max limit if specified
          // if (cleanValue !== undefined && max !== undefined && cleanValue > max) {
          //   cleanValue = max
          // }

          // Update local state
          setDisplayValue(cleanValue.toString())

          // Call onChange callback
          onChange(cleanValue)

          if (timeout.current) {
            clearTimeout(timeout.current)
          }

          timeout.current = setTimeout(() => {
            if (cleanValue !== undefined && cleanValue > 0) {
              onAmountChange?.(
                tab === 'buy'
                  ? Number(cleanValue) * targetScale
                  : Number(cleanValue),
              )
            }
          }, 1000)
        }}
        className={cn(
          placeholder && !displayValue ? 'text-end' : 'text-center',
          'ltr',
          noFocusStyle && '!bg-transparent !border-none',
        )}
        noFocusStyle={noFocusStyle}
      />
    </div>
  )
}
